import { BellOutlined, CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Badge, Button, Tooltip, Typography } from 'antd';
import type React from 'react';
import { useCallback, useState } from 'react';
import { AppModal } from '@/components/ui/AppModal';
import type { NotificationPermissionStatus } from '@/services/fcm/types';

const { Paragraph, Title, Text } = Typography;

interface NotificationStatusIndicatorProps {
  permissionStatus: NotificationPermissionStatus;
  onEnableClick: () => void;
  isLoading?: boolean;
}

const getStatusConfig = (status: NotificationPermissionStatus) => {
  switch (status) {
    case 'granted':
      return {
        color: '#52c41a',
        icon: <BellOutlined style={{ fontSize: 18 }} />,
        tooltip: 'Push notifications enabled',
        showButton: false,
      };
    case 'default':
      return {
        color: '#faad14',
        icon: <BellOutlined style={{ fontSize: 18 }} />,
        tooltip: 'Click to enable notifications',
        showButton: true,
        buttonText: 'Enable',
      };
    case 'denied':
      return {
        color: '#ff4d4f',
        icon: <CloseCircleOutlined style={{ fontSize: 18 }} />,
        tooltip: 'Notifications blocked',
        showButton: true,
        buttonText: 'Unblock',
      };
    case 'no-token':
      return {
        color: '#ff4d4f',
        icon: <BellOutlined style={{ fontSize: 18 }} />,
        tooltip: 'Click to reload and initialize notifications',
        showButton: true,
        buttonText: 'Reload',
      };
    case 'unsupported':
      return {
        color: '#d9d9d9',
        icon: <WarningOutlined style={{ fontSize: 18 }} />,
        tooltip: 'Browser does not support notifications',
        showButton: false,
      };
  }
};

const InstructionsModal: React.FC<{
  visible: boolean;
  onClose: () => void;
}> = ({ visible, onClose }) => {
  return (
    <AppModal
      title="How to Enable Push Notifications"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Got it
        </Button>,
      ]}
      width={600}
    >
      <div>
        <Paragraph>
          Push notifications are currently blocked in your browser. Follow these steps to enable
          them:
        </Paragraph>

        <Title level={5}>Google Chrome</Title>
        <Paragraph>
          1. Click the lock icon <Text strong>🔒</Text> in the address bar
          <br />
          2. Find <Text strong>Notifications</Text> and change to <Text strong>Allow</Text>
          <br />
          3. Reload the page
        </Paragraph>

        <Paragraph>
          <Text type="secondary">
            Alternative: Settings → Privacy and security → Site Settings → Notifications
          </Text>
        </Paragraph>

        <Title level={5}>Mozilla Firefox</Title>
        <Paragraph>
          1. Click the lock icon <Text strong>🔒</Text> in the address bar
          <br />
          2. Click the <Text strong>X</Text> next to blocked permissions
          <br />
          3. Allow notifications when prompted and reload the page
        </Paragraph>

        <Paragraph>
          <Text type="secondary">
            Alternative: Settings → Privacy & Security → Permissions → Notifications → Settings
          </Text>
        </Paragraph>

        <Title level={5}>Safari</Title>
        <Paragraph>
          1. Open Safari <Text strong>Preferences</Text>
          <br />
          2. Go to <Text strong>Websites</Text> tab
          <br />
          3. Select <Text strong>Notifications</Text>
          <br />
          4. Find this website and change to <Text strong>Allow</Text>
          <br />
          5. Reload the page
        </Paragraph>

        <Paragraph>
          <Text type="secondary">
            Alternative: Safari → Settings for This Website → Notifications → Allow
          </Text>
        </Paragraph>
      </div>
    </AppModal>
  );
};

export const NotificationStatusIndicator: React.FC<NotificationStatusIndicatorProps> = ({
  permissionStatus,
  onEnableClick,
  isLoading = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const config = getStatusConfig(permissionStatus);

  const handleClick = useCallback(() => {
    if (permissionStatus === 'denied') {
      setIsModalVisible(true);
    } else if (permissionStatus === 'default') {
      onEnableClick();
    } else if (permissionStatus === 'no-token') {
      window.location.reload();
    }
  }, [permissionStatus, onEnableClick]);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  if (permissionStatus === 'unsupported') {
    return (
      <Tooltip title={config.tooltip}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginLeft: 16,
            marginRight: 16,
            color: config.color,
          }}
        >
          {config.icon}
        </div>
      </Tooltip>
    );
  }

  if (!config.showButton) {
    return (
      <Tooltip title={config.tooltip}>
        <Badge
          status="success"
          style={{
            marginLeft: 16,
            marginRight: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              color: config.color,
            }}
          >
            {config.icon}
          </div>
        </Badge>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title={config.tooltip}>
        <Button
          type="text"
          size="small"
          icon={config.icon}
          onClick={handleClick}
          loading={isLoading}
          style={{
            marginLeft: 8,
            marginRight: 8,
            color: config.color,
            borderColor: config.color,
          }}
        >
          {config.buttonText}
        </Button>
      </Tooltip>
      <InstructionsModal visible={isModalVisible} onClose={handleModalClose} />
    </>
  );
};
